import { AuctionType } from '~/types';

/**
 * Lot status for TIMED (TimeOnline) auctions.
 *
 * Timed auctions have no auctioneer and no seller console — the clock closes
 * each lot at its own `cutOffTime`, and `end_intervals_enabled` staggers those
 * cutoffs so lots close one at a time over the closing window.
 *
 * The rule, as specified by the client: a TimeOnline lot displays SOLD or
 * UNSOLD **at its own time zero** — the moment its own `cutOffTime` passes.
 * Not at the auction's `auctionEnd`, and not once every lot has closed.
 *
 * The defect this guards against is a stamp appearing *before* time zero. That
 * has two causes, and this helper only addresses the first:
 *
 *   1. Reading the wrong clock. The pre-existing inline logic keyed off the
 *      auction's `auctionEnd`, which for a staggered sale is neither the lot's
 *      cutoff nor a value the lot list can rely on (TIME2LIVE stores a 2099
 *      sentinel). This helper reads the lot's own cutoff and handles the
 *      sentinel explicitly.
 *   2. Reading stale data. Every surface holds loader data captured at page
 *      load, so a card flipping at time zero decides SOLD vs UNSOLD from a
 *      `bidCount` that may be hours old. That is fixed separately, by
 *      revalidating at lot close — see `useRevalidateOnLotClose`.
 *
 * Returns `null` for every non-timed auction type so that Time2Live and
 * Live Auction keep their existing rendering untouched. Their stamps are
 * driven by the auctioneer pressing sold/unsold, which is a separate path.
 */

export type LotPhase = 'scheduled' | 'open' | 'closed';
export type LotOutcome = 'sold' | 'unsold' | null;

export type LotStatus = {
  phase: LotPhase;
  /** Non-null once the lot's own cutoff has passed. */
  outcome: LotOutcome;
  /** The lot's own end time; null when unknown or a sentinel placeholder. */
  endsAt: Date | null;
  /**
   * True once every lot in the auction has closed.
   *
   * Informational only — it no longer gates the outcome. Retained because it
   * is cheap, already plumbed through the read queries, and describes the
   * auction-wide state that callers scheduling work off the closing window
   * may want. Nothing consumes it today.
   */
  auctionConcluded: boolean;
};

export type LotStatusInput = {
  auctionType: string | null | undefined;
  auctionStart: Date | string | null | undefined;
  /** This lot's own cutoff. Time zero for the sold/unsold stamp. */
  cutOffTime: Date | string | null | undefined;
  /** MAX(cut_off_time) across every lot in the auction. */
  auctionLastCutOff: Date | string | null | undefined;
  bidCount: number | null | undefined;
  /**
   * Optional reserve refinement. `reservedPrice` is not currently selected by
   * any list query, so it is normally undefined and the outcome falls through
   * to the bid-count rule. That matches today's behaviour: a below-reserve lot
   * still reads SOLD. Fixing that needs the reserve joined into the read paths
   * and is deliberately out of scope for this change.
   */
  highestBidAmount?: number | null;
  reservedPrice?: number | null;
  /**
   * Never true for a timed lot in production — it is only written from the
   * live console, which timed auctions cannot reach. Honoured anyway so the
   * helper cannot manufacture a sale over an explicit unsold marking.
   */
  lotUnsold?: boolean | null;
};

/** TIME2LIVE auctions store 2099-12-31 because auctionEnd is NOT NULL. */
const SENTINEL_YEAR = 2099;

const toDate = (value: Date | string | null | undefined): Date | null => {
  if (value === null || value === undefined) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const isSentinel = (date: Date | null): boolean =>
  date !== null && date.getUTCFullYear() >= SENTINEL_YEAR;

export function getTimedLotStatus(input: LotStatusInput, now: Date = new Date()): LotStatus | null {
  if (input.auctionType !== AuctionType.TIMEONLINE) return null;

  const cutOff = toDate(input.cutOffTime);
  const endsAt = isSentinel(cutOff) ? null : cutOff;
  const lastCutOff = toDate(input.auctionLastCutOff);

  const auctionConcluded =
    lastCutOff !== null && !isSentinel(lastCutOff) && now.getTime() >= lastCutOff.getTime();

  // No usable cutoff — treat as open rather than inventing a terminal state.
  if (endsAt === null) {
    return { phase: 'open', outcome: null, endsAt: null, auctionConcluded };
  }

  const auctionStart = toDate(input.auctionStart);
  const closed = now.getTime() >= endsAt.getTime();

  let phase: LotPhase;
  if (closed) phase = 'closed';
  else if (auctionStart !== null && now.getTime() < auctionStart.getTime()) phase = 'scheduled';
  else phase = 'open';

  return { phase, outcome: resolveOutcome(input, phase), endsAt, auctionConcluded };
}

function resolveOutcome(input: LotStatusInput, phase: LotPhase): LotOutcome {
  // Time zero is the lot's own cutoff. Before that, no terminal claim.
  if (phase !== 'closed') return null;

  if (input.lotUnsold === true) return 'unsold';

  const bidCount = input.bidCount ?? 0;
  if (bidCount <= 0) return 'unsold';

  const reserve = input.reservedPrice;
  if (reserve !== undefined && reserve !== null && reserve > 0) {
    if ((input.highestBidAmount ?? 0) < reserve) return 'unsold';
  }

  return 'sold';
}
